/**
 * Keeps the org segment of every org route sound (`/$org/...`): mounted once
 * in the signed-in app shell (`AppSidebarLayout`), so it covers the org pages
 * outside the chat layout too (Workflows, Spaces, Compliance, ...).
 *
 * @module components/OrgSegmentCanonicalizer
 */
import { useRouter, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

import { canonicalOrgSegment, misplacedOrgPath, orgUrlSegment, replaceOrgSegmentInPath } from "../orgRouting";
import { readLastPickedOrgId, useOrgs } from "../state/orgs";

/**
 * Canonicalize the org URL segment: links built before the orgs snapshot
 * loads (server-welcome bootstrap, old bookmarks) carry the org *id* — once
 * the directory knows the slug, replace it in place. Slug-less and unknown
 * segments are left alone. The rewrite works on the current location's
 * pathname (history-level replace) rather than a relative route navigation,
 * so the active child route — thread, draft, or index — plus search and
 * hash survive verbatim.
 */
export function OrgSegmentCanonicalizer() {
  const router = useRouter();
  // The deepest match carries every param of the location; mounted at the root, `useParams` would see only the root's.
  const segment = useRouterState({
    select: (state) => (state.matches.at(-1)?.params as { org?: string } | undefined)?.org ?? null,
  });
  const { orgs } = useOrgs();
  useEffect(() => {
    if (segment === null || orgs.length === 0) {
      return;
    }
    // `/undefined/compliance/...` is a link built from a missing org, and
    // `/chat/workflows` one whose org gave way to a section: neither asks for
    // an org called "undefined" or "chat". Send it to the org the user last
    // worked in (or their first), keeping the rest of the path.
    const lastPicked = readLastPickedOrgId();
    const target = orgs.find((org) => org.id === lastPicked) ?? orgs[0];
    const misplaced = target
      ? misplacedOrgPath(
          router.state.location.pathname,
          segment,
          orgs.map((org) => ({ id: org.id, slug: org.slug })),
          orgUrlSegment(target.id),
        )
      : null;
    if (misplaced !== null) {
      const location = router.state.location;
      router.history.replace(`${misplaced}${location.searchStr}${location.hash ? `#${location.hash}` : ""}`);
      return;
    }
    const canonical = canonicalOrgSegment(
      segment,
      orgs.map((org) => ({ id: org.id, slug: org.slug })),
    );
    if (canonical === null || canonical === segment) {
      return;
    }
    const location = router.state.location;
    const pathname = replaceOrgSegmentInPath(location.pathname, segment, canonical);
    if (pathname !== null) {
      router.history.replace(
        `${pathname}${location.searchStr}${location.hash ? `#${location.hash}` : ""}`,
      );
    }
  }, [orgs, router, segment]);
  return null;
}
